import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { publicApi } from "@/lib/api";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import PublicLayout from "@/components/public/PublicLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { titleCase, fmtDate } from "@/lib/format";
import MilitaryVerifyCard from "@/components/public/MilitaryVerifyCard";
import { Crown, ArrowRight } from "lucide-react";

export default function CustomerAccount() {
  const { customer } = useCustomerAuth();
  const [data, setData] = useState(null);
  useEffect(() => { publicApi.get("/auth/account").then((r) => setData(r.data)).catch(() => {}); }, []);

  const tier = data?.tier;
  const c = data?.customer || customer;

  return (
    <PublicLayout>
      <section className="max-w-4xl mx-auto px-5 py-16" data-testid="customer-account">
        <div className="text-[#c9a86a] tracking-[0.3em] text-xs mb-2">CLIENT LEDGER</div>
        <h1 className="text-3xl font-semibold text-[var(--text)]">Welcome, {c?.name?.split(" ")[0]}</h1>

        {/* Tier card */}
        <div className="mt-8 border border-[#c9a86a] bg-[var(--panel-2)] rounded-xl p-6 md:p-8">
          <div className="flex items-center gap-2 text-[#c9a86a]"><Crown size={18} /><span className="tracking-widest text-xs">YOUR TIER</span></div>
          <div className="text-2xl font-semibold text-[var(--text)] mt-2" data-testid="account-tier">{c?.vip_tier_name || tier?.name}</div>
          {tier && <p className="text-[var(--text-2)] mt-2">{tier.description}</p>}
          {tier && (
            <div className="flex flex-wrap gap-2 mt-4">
              {tier.benefits.map((b) => <Badge key={b} variant="outline" className="bg-[var(--bg)] text-[#c9a86a] border-[var(--border-2)]">{b}</Badge>)}
            </div>
          )}
          <div className="mt-5">
            <Link to="/vip"><Button variant="outline" className="border-[var(--border-2)] text-[var(--text-2)] hover:text-[var(--text)] rounded-none">Update my preferences</Button></Link>
          </div>
        </div>

        {/* Military verification (ID.me) */}
        <div className="mt-8 max-w-md">
          <MilitaryVerifyCard />
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-8">
          {/* Profile */}
          <div className="border border-[var(--border-2)] bg-[var(--panel)] rounded-xl p-6">
            <h2 className="text-[var(--text)] font-medium mb-4">Your Profile</h2>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between border-b border-[var(--border-lch)] pb-2"><dt className="text-[var(--muted)]">Email</dt><dd className="text-[var(--text-2)]">{c?.email}</dd></div>
              {c?.phone && <div className="flex justify-between border-b border-[var(--border-lch)] pb-2"><dt className="text-[var(--muted)]">Phone</dt><dd className="text-[var(--text-2)]">{c.phone}</dd></div>}
              {c?.budget_range && <div className="flex justify-between border-b border-[var(--border-lch)] pb-2"><dt className="text-[var(--muted)]">Budget</dt><dd className="text-[var(--text-2)]">{c.budget_range}</dd></div>}
              {c?.buying_timeline && <div className="flex justify-between border-b border-[var(--border-lch)] pb-2"><dt className="text-[var(--muted)]">Timeline</dt><dd className="text-[var(--text-2)]">{c.buying_timeline}</dd></div>}
              {c?.favorite_brands?.length > 0 && <div className="border-b border-[var(--border-lch)] pb-2"><dt className="text-[var(--muted)] mb-1">Favorite Brands</dt><dd className="text-[var(--text-2)]">{c.favorite_brands.join(", ")}</dd></div>}
            </dl>
          </div>

          {/* Requests */}
          <div className="border border-[var(--border-2)] bg-[var(--panel)] rounded-xl p-6">
            <h2 className="text-[var(--text)] font-medium mb-4">Your Watch Requests</h2>
            {data?.requests?.length ? (
              <div className="space-y-2">
                {data.requests.map((r) => (
                  <div key={r.id} className="flex justify-between items-center border-b border-[var(--border-lch)] pb-2">
                    <span className="text-sm text-[var(--text-2)]">{[r.brand_wanted, r.model_wanted].filter(Boolean).join(" ") || "Request"}</span>
                    <Badge variant="outline" className="bg-[var(--bg)] text-[var(--text-2)] border-[var(--border-2)] text-[10px]">{titleCase(r.status)}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-[var(--muted)]">
                <p>No requests yet.</p>
                <p className="mt-3 text-[var(--text-2)]">Looking for something specific? Our concierge sourcing opens soon.</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
